import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";

import { MdDeleteOutline, MdOutlineModeEdit } from "react-icons/md";
import { BiShow } from "react-icons/bi";
import { useGetAllUsersQuery } from "../../../redux/slices/admin/workers/allUserSlince.js";
import { useUpdateEmployeeMutation, useDeleteEmployeeMutation } from "../../../redux/slices/admin/workers/newEmployeeSlince.js";
import { useOutletContext, useLocation } from "react-router-dom";
import "../../../styles/admin.css/workers-css/manage-employees.css";

const cities = [
  "Ağdam", "Ağdaş", "Ağcabədi", "Ağstafa", "Ağsu", "Astara", "Babək", "Bakı", "Balakən", "Bərdə",
  "Beyləqan", "Biləsuvar", "Daşkəsən", "Füzuli", "Gədəbəy", "Gəncə", "Goranboy", "Göyçay", "Göygöl",
  "Hacıqabul", "İmişli", "İsmayıllı", "Cəbrayıl", "Culfa", "Kəlbəcər", "Xaçmaz", "Xankəndi", "Xocavənd",
  "Xırdalan", "Kürdəmir", "Lənkəran", "Lerik", "Masallı", "Mingəçevir", "Naxçıvan", "Naftalan", "Neftçala",
  "Oğuz", "Ordubad", "Qəbələ", "Qax", "Qazax", "Quba", "Qubadlı", "Qusar", "Saatlı", "Sabirabad", "Şabran",
  "Şahbuz", "Şəki", "Şamaxı", "Şəmkir", "Şərur", "Şirvan", "Siyəzən", "Şuşa", "Sumqayıt", "Tərtər", "Tovuz",
  "Ucar", "Yardımlı", "Yevlax", "Zaqatala", "Zərdab", "Zəngilan"
];

const ITEM_HEIGHT = 48;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + 8,
      width: 250,
    },
  },
};

export default function ManageEmployees() {
  const { sidebarOpen } = useOutletContext();
  const location = useLocation();
  const highlightId = location.state?.highlightId;
  
  const { data: employeesResponse = [], isLoading, refetch } = useGetAllUsersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const employees = employeesResponse?.data || employeesResponse || [];
  const [updateEmployee, { isLoading: isUpdating }] = useUpdateEmployeeMutation();

  const [deleteEmployee] = useDeleteEmployeeMutation();

  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [deletingId, setDeletingId] = useState(null);
  const [isUpdatingButton, setIsUpdatingButton] = useState(false);
  const [search, setSearch] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [mode, setMode] = useState(null);
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (highlightId && employees.length > 0) {
      const employee = employees.find(emp => emp.id === highlightId);
      if (employee) {
        setOpenRow(employee.id);
        setMode("show");
        // Clear the highlightId after handling
        window.history.replaceState({}, document.title);
      }
    }
  }, [highlightId, employees]);


  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const filteredEmployees = employees.filter((emp) => {
    const fullName = `${emp.name} ${emp.surname}`.toLowerCase();
    return (
      fullName.includes(search.toLowerCase()) ||
      emp.personalMail?.toLowerCase().includes(search.toLowerCase()) ||
      emp.phoneNumber?.includes(search)
    );
  });

  // When highlighting, show all employees including the highlighted one
  const displayEmployees = highlightId 
    ? employees 
    : filteredEmployees;

  const handleShowClick = (emp) => {
    if (openRow === emp.id && mode === "show") {
      setOpenRow(null);
      setMode(null);
    } else {
      setOpenRow(emp.id);
      setMode("show");
      setEditValues({});
    }
  };

  const handleEditClick = (emp) => {
    if (openRow === emp.id && mode === "edit") {
      setOpenRow(null);
      setMode(null);
      setEditValues({});
    } else {
      setOpenRow(emp.id);
      setMode("edit");

      const cleanPhone = emp.phoneNumber ? String(emp.phoneNumber).replace("+994", "").trim() : "";
      setEditValues({
        ...emp,
        phoneNumber: cleanPhone
      });
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteEmployee(id).unwrap();
      setToast({ open: true, severity: "success", message: "İşçi uğurla silindi!" });
      refetch();
    } catch (error) {
      setToast({ open: true, severity: "error", message: "Silmək mümkün olmadı!" });
    } finally {
      setDeletingId(null);
    }
  };


  const handleInputChange = (field, value) => {
    const prevValue = String(editValues[field] || "");


    if (value.length < prevValue.length || value === "") {
      setEditValues((prev) => ({ ...prev, [field]: value }));
      return;
    }

    let isValid = false;


    if (field === "name" || field === "surname") {
      isValid = /^[a-zA-ZÇçƏəĞğİıÖöŞşÜü\s]*$/.test(value);
    }

    else if (field === "personalMail") {
      isValid = /^[a-zA-Z0-9.@]*$/.test(value);
    }

    else if (field === "phoneNumber") {
      isValid = /^[0-9]*$/.test(value) && value.length <= 9;
    }

    else if (field === "address1" || field === "address2") {
      isValid = /^[a-zA-Z0-9ÇçƏəĞğİıÖöŞşÜü\s.]*$/.test(value);
    }
    else {
      isValid = true;
    }

    if (isValid) {
      setEditValues((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    setIsUpdatingButton(true);
    const finalData = {
      updatedUserId: editValues.id,
      phoneNumber: editValues.phoneNumber,
      firstName: editValues.name,
      lastName: editValues.surname,
      email: editValues.personalMail,
      city: editValues.city,
      adress1: editValues.address1,
      adress2: editValues.address2,
    };

    try {

      await updateEmployee(finalData).unwrap();


      setToast({ open: true, severity: "success", message: "Məlumat uğurla yeniləndi!" });
      refetch();


      setOpenRow(null);
      setMode(null);
      setEditValues({});
    } catch (error) {
      setToast({ open: true, severity: "info", message: "Yeniləmək mümkün olmadı!" });
    }
    finally {
      setIsUpdatingButton(false);
    }
  };

  return (
    <Box className={`employees-container ${sidebarOpen ? "sidebar-open" : ""}`}>
      <Box className="header-row">
        <Box className="left-title">
          <Typography style={{ color: "#545A61" }}>İşçilərin Siyahısı</Typography>
          <Divider className="employees-divider" />
        </Box>

        <TextField
          size="small"
          placeholder="Axtar..."
          className="search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      <TableContainer component={Paper} className="employees-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>#No</strong></TableCell>
              <TableCell><strong>Tam Ad</strong></TableCell>
              <TableCell><strong>Panel e-poçt</strong></TableCell>
              <TableCell><strong>Əlaqə nömrəsi</strong></TableCell>
              <TableCell><strong>Yaradılma tarixi</strong></TableCell>
              <TableCell><strong>Seçimlər</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} align="center">Yüklənir...</TableCell></TableRow>
            ) : displayEmployees.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">Məlumat tapılmadı</TableCell></TableRow>
            ) : (
              displayEmployees.map((emp) => (
                <React.Fragment key={emp.id}>
                  <TableRow>
                    <TableCell>{"#" + String(emp.serialNo || emp.id).padStart(4, "0")}</TableCell>
                    <TableCell>{emp.name} {emp.surname}</TableCell>
                    <TableCell>{emp.personalMail}</TableCell>
                    <TableCell><strong>+994 {emp.phoneNumber}</strong></TableCell>
                    <TableCell>{emp.createdAt ? new Date(emp.createdAt).toLocaleString() : "-"}</TableCell>
                    <TableCell className="manage-wrapper">
                      <Button variant="contained" size="small" className="btn-show" onClick={() => handleShowClick(emp)}>
                        <BiShow style={{ width: "24px", height: "24px" }} />
                      </Button>
                      <Button variant="contained" size="small" className="btn-edit" onClick={() => handleEditClick(emp)}>
                        <MdOutlineModeEdit style={{ width: "24px", height: "24px" }} />
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        className="btnn-delete"
                        onClick={() => handleDelete(emp.id)}
                        disabled={deletingId === emp.id}
                      >
                        {deletingId === emp.id ? (
                          <CircularProgress size={24} style={{ color: "white" }} />
                        ) : (
                          <MdDeleteOutline style={{ width: "24px", height: "24px" }} />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>

                  {openRow === emp.id && (
                    <TableRow>
                      <TableCell colSpan={6} className="expanded-row-cell">
                        <Box className="expanded-details">
                          {mode === "edit" ? (
                            <>
                              <TextField label="Ad" size="small" fullWidth
                                value={editValues.name || ""}
                                onChange={(e) => handleInputChange("name", e.target.value)}
                                margin="dense"
                              />
                              <TextField label="Soyad" size="small" fullWidth
                                value={editValues.surname || ""}
                                onChange={(e) => handleInputChange("surname", e.target.value)}
                                margin="dense"
                              />

                              <TextField label="Panel e-poçt" size="small" fullWidth
                                value={editValues.personalMail || ""}
                                onChange={(e) => handleInputChange("personalMail", e.target.value)}
                                margin="dense"
                              />

                              <TextField
                                label="Əlaqə nömrəsi"
                                size="small"
                                fullWidth
                                value={editValues.phoneNumber || ""}
                                onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                                margin="dense"
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">+994</InputAdornment>,
                                }}
                              />

                              <TextField label="Ünvan" size="small" fullWidth
                                value={editValues.address1 || ""}
                                onChange={(e) => handleInputChange("address1", e.target.value)}
                                margin="dense"
                              />
                              <TextField label="Ünvan 2" size="small" fullWidth
                                value={editValues.address2 || ""}
                                onChange={(e) => handleInputChange("address2", e.target.value)}
                                margin="dense"
                              />

                              <FormControl fullWidth size="small" margin="dense">
                                <InputLabel>Şəhər</InputLabel>
                                <Select
                                  value={editValues.city || ""}
                                  label="Şəhər"
                                  onChange={(e) => handleInputChange("city", e.target.value)}
                                  MenuProps={MenuProps}
                                >
                                  {cities.map((city) => (
                                    <MenuItem key={city} value={city}>{city}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              <Box className="edit-actions" sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                <Button variant="outlined" size="small" onClick={() => { setOpenRow(null); setMode(null); }}>Ləğv et</Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="primary"
                                  onClick={handleSave}
                                  disabled={isUpdatingButton}
                                >
                                  {isUpdatingButton ? "Gözləyin..." : "Təsdiqlə"}
                                </Button>
                              </Box>
                            </>
                          ) : (
                            <>
                              <div><strong>Ad:</strong> {emp.name}</div>
                              <div><strong>Soyad:</strong> {emp.surname}</div>
                              <div><strong>Əlaqə nömrəsi:</strong> +994 {emp.phoneNumber}</div>
                              <div><strong>Ünvan:</strong> {emp.address1}</div>
                              <div><strong>Ünvan 2:</strong> {emp.address2}</div>
                              <div><strong>Şəhər:</strong> {emp.city}</div>
                              <div><strong>Panel e-poçt:</strong> {emp.personalMail} </div>
                              <div><strong>Panel şifrə:</strong> ******</div>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}